/**
 * Agent Insights query keys, in a module with no React or fetcher imports.
 *
 * These exist so a Server Component and a client hook can share one definition
 * of a key without the server pulling in client-only code. Importing any
 * binding from `hooks/useTeamInsightsOverview.ts` drags the whole module, and
 * that module imports `useEffect`, so a Server Component doing it is a build
 * error ("You're importing a module that depends on `useEffect` into a React
 * Server Component module"). A hand-copied key on the server would avoid the
 * error and reintroduce the drift this is meant to prevent: a key that differs
 * by one element is a silent cache miss, so the prefetch runs, the client
 * refetches anyway, and nothing errors.
 *
 * Keys are pure data and belong here. Fetchers are environment-specific and do
 * not: the client goes through `getWithAuth` (needs `window`, `localStorage`,
 * `next-auth/react`), the server through `serverBackendGet`.
 */

/**
 * Overview bundle, keyed on the org and the window in days.
 *
 * `days ?? 'all'` rather than `days` so an explicit "all time" (null) and an
 * unset window collapse to the same entry, which is what the client hook has
 * always done.
 */
export const agentInsightsOverviewBundleKey = (
  orgUuid: string,
  days?: number | null,
  teamUuid?: string | null
) =>
  [
    'agentInsightsOverviewBundle',
    orgUuid,
    days ?? 'all',
    teamUuid ?? null,
  ] as const;

/** Members grid roster (`agentInsights/members/`). `days ?? null`, not `'all'`:
 * this is what `useAgentInsightsMembers` has always keyed on, and the grid
 * and the member page must land on the same entry. */
export const agentInsightsMembersKey = (
  orgUuid: string,
  days?: number | null,
  teamUuid?: string | null
) => ['agentInsightsMembers', orgUuid, days ?? null, teamUuid ?? null] as const;

/** One engineer's drill-down (`agentInsightsMemberDetail/`), windowed. */
export const agentInsightsMemberDetailKey = (
  orgUuid: string,
  userUuid: string | null,
  days?: number | null
) => ['agentInsightsMemberDetail', orgUuid, userUuid, days ?? 'all'] as const;

/** Org goals + progress (`agentInsights/goals/`). Team scope folds to `null`. */
export const agentInsightsGoalsKey = (
  orgUuid: string,
  teamUuid?: string | null
) => ['agentInsightsGoals', orgUuid, teamUuid ?? null] as const;

/** Mutation key for an org's goals save, so the setup dialog can see it in flight. */
export const agentInsightsGoalsSaveKey = (orgUuid: string) =>
  ['agentInsightsGoalsSave', orgUuid] as const;
